import React, { useState } from 'react';
import SpeciesAutocomplete from './SpeciesAutocomplete';
import MoreDetailsSection from './MoreDetailsSection';
import { localToday, seasonFromDate, resolveHarvestCategory } from './harvestFlow';
import './GuidedHarvestForm.css';

let nextRowId = 0;

function newCatch(resource) {
  return {
    draftId: ++nextRowId,
    species: resource.species || '', speciesId: resource.speciesId || '',
    category: resource.harvestCategory || '', quantity: '1', unit: '',
    subcategory: '', abundance: '',
    notes: '', harvestDetails: { resourceOutcome: 'kept', sizeValue: '', sizeUnit: 'in', sex: '', condition: '' },
  };
}

function Control({ label, children }) {
  return <label className="guided-harvest__field"><span>{label}</span>{children}</label>;
}

export default function GuidedHarvestForm({
  resource, taxonomy, draft, onDraftChange, onSubmit, loading = false, error = '',
  onBackSpecies, onBackLocation, assistedMode = false, canCreateSpecies = false,
}) {
  const [step, setStep] = useState(3);
  const [validation, setValidation] = useState('');
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [locationApproximate, setLocationApproximate] = useState(false);
  const [initialValues] = useState(() => ({
    harvestDate: localToday(), catches: [newCatch(resource)],
    observations: '', locationDescription: '', traditionalKnowledge: '',
    isTraditionalUse: false, harvestDetails: {},
  }));
  const values = draft || initialValues;
  const change = (patch) => { onDraftChange({ ...values, ...patch }); setValidation(''); };
  const changeRow = (index, patch) => change({
    catches: values.catches.map((row, i) => i === index ? { ...row, ...patch } : row),
  });
  const detail = (index, field, value) => changeRow(index, {
    harvestDetails: { ...values.catches[index].harvestDetails, [field]: value },
  });
  const categoryLabel = (key) => taxonomy?.categories?.find((entry) => entry.key === key)?.label || key;
  const enabled = (field, category = resource.harvestCategory) => (
    !Array.isArray(taxonomy?.questionVisibility?.[field])
    || taxonomy.questionVisibility[field].includes(category)
  );
  const sharedEnabled = (field) => values.catches.some((row) => enabled(field, row.category));
  const submission = () => ({
    ...values,
    ...(assistedMode ? {
      locationConfirmed: true,
      locationPrecision: locationApproximate ? 'approximate' : 'confirmed',
    } : {}),
    ...Object.fromEntries(['observations', 'locationDescription', 'traditionalKnowledge', 'isTraditionalUse'].map((field) => [
      field, sharedEnabled(field) ? values[field] : field === 'isTraditionalUse' ? false : '',
    ])),
    harvestDetails: {
      uses: sharedEnabled('uses') ? values.harvestDetails?.uses : [],
      otherUse: sharedEnabled('uses') ? values.harvestDetails?.otherUse : '',
      reproductiveSignsObserved: sharedEnabled('reproductiveSignsObserved') && !!values.harvestDetails?.reproductiveSignsObserved,
      observedSigns: sharedEnabled('reproductiveSignsObserved') ? values.harvestDetails?.observedSigns || [] : [],
      unusualConditions: sharedEnabled('unusualConditions') ? values.harvestDetails?.unusualConditions : '',
      followUpRequested: sharedEnabled('followUpRequested') && !!values.harvestDetails?.followUpRequested,
    },
    catches: values.catches.map((row) => ({
      ...row,
      subcategory: enabled('subcategory', row.category) ? row.subcategory : '',
      abundance: enabled('abundance', row.category) ? row.abundance : '',
      notes: enabled('rowNotes', row.category) ? row.notes : '',
      harvestDetails: {
        ...row.harvestDetails,
        sizeValue: enabled('size', row.category) ? row.harvestDetails.sizeValue : '',
        sizeUnit: enabled('size', row.category) ? row.harvestDetails.sizeUnit : '',
        sex: enabled('sex', row.category) ? row.harvestDetails.sex : '',
        condition: enabled('condition', row.category) ? row.harvestDetails.condition : '',
      },
    })),
  });

  const validDetails = () => {
    if (!values.harvestDate || !seasonFromDate(values.harvestDate)) {
      setValidation('Choose the harvest date.'); return false;
    }
    for (const [index, row] of values.catches.entries()) {
      const animal = ['fishing', 'hunting', 'birds', 'trapping'].includes(row.category);
      if (!row.speciesId || !row.species || !row.category) {
        setValidation(`Row ${index + 1}: select a species from the shared catalogue with a harvest category.`); return false;
      }
      if (!Number.isFinite(Number(row.quantity)) || Number(row.quantity) <= 0
        || (animal && !Number.isInteger(Number(row.quantity)))) {
        setValidation(`Row ${index + 1}: enter a positive ${animal ? 'whole-number count' : 'quantity'}.`); return false;
      }
      if (!animal && !row.unit) { setValidation(`Row ${index + 1}: select a quantity unit.`); return false; }
      if (enabled('size', row.category) && row.harvestDetails.sizeValue !== '' && row.harvestDetails.sizeValue != null
        && (!Number.isFinite(Number(row.harvestDetails.sizeValue)) || Number(row.harvestDetails.sizeValue) <= 0)) {
        setValidation(`Row ${index + 1}: size must be greater than zero.`); return false;
      }
    }
    return true;
  };

  return (
    <form aria-label="Guided harvest log" className="guided-harvest" onSubmit={(event) => {
      event.preventDefault();
      if (assistedMode && !locationConfirmed) {
        setValidation('Read back the selected map point and confirm the caller agrees before continuing.');
        return;
      }
      if (step === 3) { if (validDetails()) { onDraftChange(values); setStep(4); } }
      else if (validDetails()) onSubmit(submission());
    }}>
      <nav className="guided-harvest__back" aria-label="Previous harvest steps">
        <button type="button" disabled={loading} onClick={onBackSpecies}>Back to species</button>
        <button type="button" disabled={loading} onClick={() => { onDraftChange(values); onBackLocation(); }}>Change location</button>
      </nav>
      <p><strong>{resource.species}</strong> · {categoryLabel(resource.harvestCategory)}</p>
      <h4>Step {step} of 4 — {step === 3 ? 'Harvest details' : 'Notes and review'}</h4>
      {(error || validation) && <p role="alert" className="guided-harvest__error">{error || validation}</p>}
      {assistedMode && (
        <div className="guided-harvest__catch">
          <label>
            <input type="checkbox" checked={locationConfirmed} onChange={(event) => setLocationConfirmed(event.target.checked)} />
            {' '}I read back this map point and the caller confirmed it.
          </label>
          <label style={{ display: 'block', marginTop: 8 }}>
            <input type="checkbox" checked={locationApproximate} onChange={(event) => setLocationApproximate(event.target.checked)} />
            {' '}This is an approximate location; do not treat it as an exact harvest site.
          </label>
        </div>
      )}
      <fieldset disabled={loading}>
        {step === 3 ? (
          <>
            <Control label="Harvest date"><input type="date" required value={values.harvestDate} onChange={(e) => change({ harvestDate: e.target.value })} /></Control>
            <p>Season: <strong>{seasonFromDate(values.harvestDate)}</strong> (from the date)</p>
            <p>Each row groups catches with the same species, size, sex and outcome. Split them into rows if any detail differs.</p>
            {values.catches.map((row, index) => {
              const animal = ['fishing', 'hunting', 'birds', 'trapping'].includes(row.category);
              return (
                <fieldset key={row.draftId} className="guided-harvest__catch">
                  <legend>Harvest row {index + 1} — {row.species}</legend>
                  {index > 0 && (
                    <div>
                      <span>Species for this row</span>
                      <SpeciesAutocomplete canCreate={canCreateSpecies} value={row.species} onSelect={(species) => changeRow(index, {
                        species: species.speciesName, speciesId: species.speciesId || '',
                        category: resolveHarvestCategory(species, taxonomy),
                        subcategory: '', abundance: '',
                      })} onInputChange={(species) => changeRow(index, {
                        species, speciesId: '', category: '', subcategory: '', abundance: '',
                      })} />
                      <small>{categoryLabel(row.category) || 'Select a recognized species to set its category.'}</small>
                      {!row.category && <Control label="Category for this unclassified resource">
                        <select value="" onChange={(e) => changeRow(index, { category: e.target.value })}>
                          <option value="">Choose category</option>
                          {(taxonomy?.categories || []).map((category) => <option key={category.key} value={category.key}>{category.label}</option>)}
                        </select>
                      </Control>}
                    </div>
                  )}
                  <div className="guided-harvest__grid">
                    <Control label={animal ? 'Number caught / harvested' : 'Quantity harvested'}>
                      <input type="number" min={animal ? 1 : 0.001} step={animal ? 1 : 'any'} required value={row.quantity} onChange={(e) => changeRow(index, { quantity: e.target.value })} />
                    </Control>
                    {!animal && <Control label="Quantity unit"><select value={row.unit} onChange={(e) => changeRow(index, { unit: e.target.value })}>
                      <option value="">Choose unit</option>
                      {(taxonomy?.units || []).map((unit) => <option key={unit}>{unit}</option>)}
                    </select></Control>}
                    {enabled('size', row.category) && <><Control label="Size / weight (optional)"><input type="number" min="0" step="any" value={row.harvestDetails.sizeValue} onChange={(e) => detail(index, 'sizeValue', e.target.value)} /></Control>
                    <Control label="Size / weight unit"><select value={row.harvestDetails.sizeUnit} onChange={(e) => detail(index, 'sizeUnit', e.target.value)}>
                      <option value="in">Inches</option><option value="cm">Centimetres</option><option value="lbs">Pounds</option><option value="kg">Kilograms</option>
                    </select></Control></>}
                    {animal && enabled('sex', row.category) && <Control label="Sex (optional)"><select value={row.harvestDetails.sex} onChange={(e) => detail(index, 'sex', e.target.value)}>
                      <option value="">Not specified</option><option value="male">Male</option><option value="female">Female</option><option value="unknown">Unknown</option>
                    </select></Control>}
                    {enabled('condition', row.category) && <Control label="Condition (optional)"><select value={row.harvestDetails.condition} onChange={(e) => detail(index, 'condition', e.target.value)}>
                      <option value="">Not specified</option><option value="healthy">Healthy</option><option value="injured">Injured</option><option value="diseased_or_unusual">Diseased or unusual</option><option value="dead_found">Dead when found</option><option value="unknown">Unknown</option>
                    </select></Control>}
                    <Control label={row.category === 'fishing' ? 'Released?' : 'Harvest outcome'}>
                      <select value={row.harvestDetails.resourceOutcome} onChange={(e) => detail(index, 'resourceOutcome', e.target.value)}>
                        <option value="kept">{row.category === 'fishing' ? 'No — kept' : 'Kept'}</option>
                        <option value="released">{row.category === 'fishing' ? 'Yes — released' : 'Released'}</option>
                        <option value="left_in_place">Left in place</option>
                      </select>
                    </Control>
                  </div>
                  {enabled('rowNotes', row.category) && <Control label="Row notes (optional)"><input value={row.notes} onChange={(e) => changeRow(index, { notes: e.target.value })} placeholder="Anything about these catches?" /></Control>}
                  <details><summary>More about this row (optional)</summary>
                    {enabled('subcategory', row.category) && !!taxonomy?.categories?.find((category) => category.key === row.category)?.subtypes?.length && (
                      <Control label={taxonomy.categories.find((category) => category.key === row.category).subcategoryLabel || 'Method / context'}><select value={row.subcategory} onChange={(e) => changeRow(index, { subcategory: e.target.value })}>
                        <option value="">Not specified</option>
                        {taxonomy.categories.find((category) => category.key === row.category).subtypes.map((method) => <option key={method}>{method}</option>)}
                      </select></Control>
                    )}
                    {enabled('abundance', row.category) && <Control label="Abundance"><select value={row.abundance} onChange={(e) => changeRow(index, { abundance: e.target.value })}>
                      <option value="">Not specified</option>
                      {(taxonomy?.abundanceOptions || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select></Control>}
                  </details>
                  {index > 0 && <button type="button" onClick={() => change({ catches: values.catches.filter((_, i) => i !== index) })}>Remove row {index + 1}</button>}
                </fieldset>
              );
            })}
            <button type="button" disabled={values.catches.length >= 50} onClick={() => change({ catches: [...values.catches, newCatch(resource)] })}>+ Add another catch / harvest row</button>
            <p>Released catches are saved, but do not increase the kept harvest total.</p>
          </>
        ) : (
          <>
            <ul>{values.catches.map((row) => <li key={row.draftId}>{row.quantity} {row.species} — {enabled('size', row.category) && row.harvestDetails.sizeValue ? `${row.harvestDetails.sizeValue} ${row.harvestDetails.sizeUnit}, ` : ''}{row.harvestDetails.resourceOutcome}</li>)}</ul>
            <p>{values.harvestDate} · {seasonFromDate(values.harvestDate)}</p>
            {sharedEnabled('observations') && <Control label="Notes about this harvest (optional)"><textarea rows={3} value={values.observations} onChange={(e) => change({ observations: e.target.value })} placeholder="For example: water was warmer than usual, fish had marks, berries were plentiful. Leave blank if there is nothing to add." /></Control>}
            {sharedEnabled('locationDescription') && <Control label="Location notes (optional)"><input value={values.locationDescription} onChange={(e) => change({ locationDescription: e.target.value })} placeholder="For example: north shore, near the creek mouth" /></Control>}
            {['traditionalKnowledge', 'isTraditionalUse', 'uses', 'reproductiveSignsObserved', 'unusualConditions', 'followUpRequested'].some(sharedEnabled) && <details><summary>More details (optional)</summary>
              {sharedEnabled('traditionalKnowledge') && <Control label="Traditional knowledge & cultural notes"><textarea value={values.traditionalKnowledge} onChange={(e) => change({ traditionalKnowledge: e.target.value })} /></Control>}
              {sharedEnabled('isTraditionalUse') && <Control label="Traditional use"><input type="checkbox" checked={values.isTraditionalUse} onChange={(e) => change({ isTraditionalUse: e.target.checked })} /></Control>}
              <MoreDetailsSection resourceUseOptions={taxonomy?.resourceUseOptions} observedSignOptions={taxonomy?.observedSignOptions} harvestOnly isFieldEnabled={sharedEnabled} category={resource.harvestCategory} values={values.harvestDetails} onChange={(harvestDetails) => change({ harvestDetails })} />
            </details>}
          </>
        )}
        <div className="guided-harvest__actions">
          {step === 4 && <button type="button" onClick={() => setStep(3)}>Back to harvest details</button>}
          <button type="submit">{loading ? 'Saving…' : step === 3 ? 'Next: notes and review' : `Save ${values.catches.length === 1 ? 'harvest' : `${values.catches.length} harvest rows`}`}</button>
        </div>
      </fieldset>
    </form>
  );
}
